import {
  MapQueryParams,
  UnreportedPlace,
  UnreportedPlaceCandidate,
} from 'infra/type'
import { crawlKakao } from 'infra/crawl-3rd-party/kakao'
import { crawlNaver } from 'infra/crawl-3rd-party/naver'
import { compareTwoStrings } from 'string-similarity'
import { isSamePlace } from 'infra/util'

export const crawl3rdParty = async (params: MapQueryParams) => {
  let [kakaoPlaces, naverPlaces] = await Promise.all([
    // 상위 15개 결과
    crawlKakao(params),
    // 상위 20개 결과
    crawlNaver(params),
  ])
  // 사용자 표기(라쿠르뜨)와 지도 표기(라크루뜨)가 다르면 한쪽 provider 결과가 비는 경우가 있음.
  // 다른 provider 상호명으로 한 번 더 검색해 합친다.
  if (kakaoPlaces.length === 0 && naverPlaces.length > 0) {
    kakaoPlaces = await crawlWithNameFallbacks(crawlKakao, params, naverPlaces)
  }
  if (naverPlaces.length === 0 && kakaoPlaces.length > 0) {
    naverPlaces = await crawlWithNameFallbacks(crawlNaver, params, kakaoPlaces)
  }
  return combine(kakaoPlaces, naverPlaces)
}

const crawlWithNameFallbacks = async (
  crawl: (params: MapQueryParams) => Promise<UnreportedPlaceCandidate[]>,
  params: MapQueryParams,
  seedPlaces: Array<{ name: string; address?: string }>,
): Promise<UnreportedPlaceCandidate[]> => {
  const tried = new Set<string>([params.query])
  const results: UnreportedPlaceCandidate[] = []
  const seen = new Set<string>()
  for (const seed of seedPlaces.slice(0, 5)) {
    for (const query of placeSearchQueries(seed.name, seed.address)) {
      if (tried.has(query)) continue
      tried.add(query)
      const candidates = await crawl({ ...params, query })
      for (const c of candidates) {
        const key = `${c.reference.provider}:${c.reference.provider_place_id || c.reference.link}`
        if (seen.has(key)) continue
        seen.add(key)
        results.push(c)
      }
      if (results.length > 0) return results
    }
  }
  return results
}

const combine = (
  kakaoPlaces: UnreportedPlaceCandidate[],
  naverPlaces: UnreportedPlaceCandidate[],
) => {
  const places: UnreportedPlace[] = naverPlaces.map((p) => ({
    name: p.name,
    coord: p.coord,
    address: p.address,
    category: p.category,
    references: [p.reference],
  }))
  kakaoPlaces.forEach((k) => {
    // 현재 카카오 장소를 모든 네이버 장소와 거리 비교
    // references 에 추가하고 filter 에서 제외
    for (let p of places) {
      if (
        // k 와 p 가 같은 장소이면서
        isSamePlace(k, p) &&
        // p 에 카카오 provider 가 추가된 적이 없을 때
        !p.references.some((r) => r.provider === 'kakao')
      ) {
        p.references.push(k.reference)
        p.address = p.address || k.address
        p.category = p.category || k.category
        // 같은 장소라고 판단되면 더 이상 해당 k 에 대한 매칭을 조사할 필요가 없음
        // 다음 k 로 넘어가기
        return
      }
    }
    // 같은 장소가 아니면 새롭게 추가
    places.push({
      name: k.name,
      coord: k.coord,
      address: k.address,
      category: k.category,
      references: [k.reference],
    })
  })
  return places
}

/** District / road tokens for disambiguating common store names (e.g. 우리슈퍼). */
const ADDRESS_TOKEN_RE =
  /([가-힣A-Za-z0-9]+구)|([가-힣A-Za-z0-9]+(?:대로|로|길))/g

const addressTokens = (address?: string | null): string[] => {
  if (!address) return []
  // Road first (more specific), then district — fewer wasted map lookups.
  const roads: string[] = []
  const districts: string[] = []
  for (const match of address.matchAll(ADDRESS_TOKEN_RE)) {
    const district = match[1]
    const road = match[2]
    if (district && !districts.includes(district)) districts.push(district)
    if (road && !roads.includes(road)) roads.push(road)
  }
  return [...roads, ...districts]
}

/** Full title, subtitle/first-token fallbacks, then name+구/도로명 for map APIs. */
export const placeSearchQueries = (
  name: string,
  address?: string | null,
): string[] => {
  const queries: string[] = []
  const push = (q: string) => {
    const trimmed = q.trim()
    if (trimmed && !queries.includes(trimmed)) queries.push(trimmed)
  }
  push(name)
  push(name.split(/[:：]/)[0] ?? '')
  const base = name.trim().split(/\s+/)[0] ?? ''
  push(base)
  const anchor = base || name.trim()
  if (anchor) {
    for (const token of addressTokens(address)) {
      push(`${anchor} ${token}`)
    }
  }
  return queries
}

const pickBestCandidate = (
  place: UnreportedPlace,
  candidates: UnreportedPlaceCandidate[],
): UnreportedPlaceCandidate | undefined => {
  const matches = candidates.filter((c) => isSamePlace(place, c))
  if (!matches.length) return undefined
  return matches.reduce((best, c) =>
    compareTwoStrings(c.name, place.name) > compareTwoStrings(best.name, place.name)
      ? c
      : best,
  )
}

export const crawlMissing3rdParty = async (
  unreportedPlace: UnreportedPlace,
): Promise<UnreportedPlace> => {
  const newReferences = [...(unreportedPlace.references || [])]
  for (const provider of ['kakao', 'naver'] as const) {
    // 기존 references 에서 현재 `provider` 가 아예 없으면 실행
    if (newReferences.some((ref) => ref.provider === provider)) continue

    let candidate: UnreportedPlaceCandidate | undefined
    for (const query of placeSearchQueries(
      unreportedPlace.name,
      unreportedPlace.address,
    )) {
      const params: MapQueryParams = {
        query,
        lat: String(unreportedPlace.coord.lat),
        lng: String(unreportedPlace.coord.lng),
      }
      const candidates =
        provider === 'kakao'
          ? await crawlKakao(params)
          : await crawlNaver(params)
      candidate = pickBestCandidate(unreportedPlace, candidates)
      if (candidate) break
    }
    if (candidate) {
      newReferences.push(candidate.reference)
    }
  }
  return {
    ...unreportedPlace,
    references: newReferences,
  }
}
